/**
 * Passenger roster domain contracts — Module 4 (TW-3447).
 *
 * A club maintains a persistent roster of players + staff with their travel-document records,
 * independent of any single trip (the trip↔passenger manifest is a later Module 4 story). As
 * elsewhere, these Zod schemas are the single source of truth for request validation: the API
 * validates bodies with them (ZodValidationPipe) and the web form validates against the same
 * objects (architecture §4.1).
 *
 * Field set mirrors the `passenger` table (data-model §6): `full_name`, `date_of_birth`,
 * `nationality`, `document_type` (the DNI/NIE/PASSPORT enum), `document_number`,
 * `document_expiry`, the `is_staff` player/staff flag, and `document_image_key` — the **private
 * DO Spaces object key** for an optional identity-document scan. The key is never sent to the
 * client (a passenger DTO carries only `hasDocumentImage`); the image is fetched through a
 * short-lived signed URL, and uploads are gated behind a feature flag (likely off initially).
 */
import { z } from "zod";

/** Identity-document types a passenger may carry (matches the Postgres `document_type` enum). */
export const DOCUMENT_TYPES = ["DNI", "NIE", "PASSPORT"] as const;
export type DocumentType = (typeof DOCUMENT_TYPES)[number];

/**
 * How many days before a document's expiry the readiness chip flips from "ok" to "expiring".
 * Set to 90 days: most destinations require several months' passport validity for entry, so a
 * document inside this window is the one a club should renew before travel. (The prototype used a
 * looser 60-day visual hint; 90 is the deliberate operational threshold for this system.)
 */
export const DOC_EXPIRY_WARNING_DAYS = 90;

/**
 * Document-readiness for a roster row, derived purely from its fields (no image needed):
 *   • `missing`  — a required field (type, number or expiry) is absent: not manifest-ready;
 *   • `expired`  — the document's expiry date is in the past;
 *   • `expiring` — expiry is within {@link DOC_EXPIRY_WARNING_DAYS} (renew before travel);
 *   • `ok`       — all fields present and expiry is comfortably in the future.
 */
export type DocumentReadiness = "ok" | "expiring" | "expired" | "missing";

/** The subset of a passenger the readiness derivation needs (date strings, `YYYY-MM-DD`). */
export interface DocumentReadinessInput {
  documentType: DocumentType | null;
  documentNumber: string | null;
  documentExpiry: string | null;
}

/**
 * Derive a passenger's document readiness against a reference instant (`nowIso`, passed in so the
 * fold stays pure and testable — never reads the clock itself, mirroring the trip/proposal folds).
 * Comparison is date-only (a document is treated as valid through its expiry date).
 */
export function documentStatus(p: DocumentReadinessInput, nowIso: string): DocumentReadiness {
  if (!p.documentType || !p.documentNumber || !p.documentExpiry) return "missing";
  const expiry = new Date(`${p.documentExpiry}T00:00:00.000Z`);
  if (Number.isNaN(expiry.getTime())) return "missing";
  const today = new Date(`${nowIso.slice(0, 10)}T00:00:00.000Z`);
  const days = Math.round((expiry.getTime() - today.getTime()) / 86_400_000);
  if (days < 0) return "expired";
  if (days <= DOC_EXPIRY_WARNING_DAYS) return "expiring";
  return "ok";
}

/** The fields a club sets on a roster row (all but the name optional — fill as known). */
const passengerFields = {
  firstName: z.string().trim().min(1, "A first name is required").max(100),
  lastName: z.string().trim().min(1, "A last name is required").max(100),
  /** Player (false) vs staff (true). */
  isStaff: z.boolean().optional(),
  dateOfBirth: z.iso.date().nullable().optional(),
  nationality: z.string().trim().max(100).nullable().optional(),
  documentType: z.enum(DOCUMENT_TYPES).nullable().optional(),
  documentNumber: z.string().trim().max(100).nullable().optional(),
  documentExpiry: z.iso.date().nullable().optional(),
};

export const createPassengerSchema = z.object({
  ...passengerFields,
  /**
   * The club to create the passenger for. Back-office actors MUST name it (they have no implied
   * club); for a club user it is ignored — the row is always pinned to their own club.
   */
  clubId: z.uuid().optional(),
});
export type CreatePassengerInput = z.infer<typeof createPassengerSchema>;

export const updatePassengerSchema = z
  .object(passengerFields)
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: "no fields to update" });
export type UpdatePassengerInput = z.infer<typeof updatePassengerSchema>;

/**
 * Confirm an uploaded document image: the client first asks for a signed upload target, PUTs the
 * bytes to it, then posts back the object `key` to attach it to the passenger. The key namespace
 * is validated server-side against the passenger (a club can never attach another club's object).
 */
export const attachDocumentImageSchema = z.object({
  key: z.string().trim().min(1).max(500),
});
export type AttachDocumentImageInput = z.infer<typeof attachDocumentImageSchema>;

/** A roster row as returned to the client. The private storage key is never exposed. */
export interface PassengerDto {
  id: string;
  clubId: string;
  firstName: string;
  lastName: string;
  /** Derived `${firstName} ${lastName}` — kept for display, sort labels and exports. */
  fullName: string;
  isStaff: boolean;
  dateOfBirth: string | null;
  nationality: string | null;
  documentType: DocumentType | null;
  documentNumber: string | null;
  documentExpiry: string | null;
  /** Whether a document image is on file (the key itself is never sent to the client). */
  hasDocumentImage: boolean;
  /** Derived document readiness at fetch time (see {@link documentStatus}). */
  documentStatus: DocumentReadiness;
  createdAt: string;
  updatedAt: string;
}

// ── Roster list pagination + sorting (server-side) ────────────────────────────
//
// The roster table pages + sorts on the server so it stays fast as a club's squad grows. The same
// `GET /api/passengers` endpoint still returns the full array when no `page` is given (the manifest
// builder + "add all players" fold over the whole roster); passing `page` switches it to the
// `PassengerListPage` envelope. `nationality`/`expiry` sort nulls-last; `expiry` sorts on the
// `documentExpiry` date.
export const PASSENGER_SORT_FIELDS = ["fullName", "nationality", "expiry"] as const;
export type PassengerSortField = (typeof PASSENGER_SORT_FIELDS)[number];

/** Roster role filter — players, staff, or everyone. */
export const ROSTER_ROLE_FILTERS = ["all", "player", "staff"] as const;
export type RosterRoleFilter = (typeof ROSTER_ROLE_FILTERS)[number];

/** Default rows per page for the roster table. */
export const DEFAULT_ROSTER_PAGE_SIZE = 20;
/** Upper bound so a caller can't ask for an unbounded page. */
export const MAX_ROSTER_PAGE_SIZE = 100;

/**
 * Query params for the paginated/sorted/filtered roster list. All optional: with no `page` the
 * endpoint returns the full array (back-compat). `role` filters players/staff; `q` is a
 * case-insensitive search whose whitespace-separated terms each match the first or last name (so
 * "Pau Quemada" finds the passenger regardless of column, and search spans every page).
 */
export const passengerListQuerySchema = z.object({
  clubId: z.uuid().optional(),
  page: z.coerce.number().int().min(1).optional(),
  pageSize: z.coerce.number().int().min(1).max(MAX_ROSTER_PAGE_SIZE).optional(),
  sort: z.enum(PASSENGER_SORT_FIELDS).optional(),
  dir: z.enum(["asc", "desc"]).optional(),
  role: z.enum(ROSTER_ROLE_FILTERS).optional(),
  q: z.string().trim().max(200).optional(),
});
export type PassengerListQuery = z.infer<typeof passengerListQuerySchema>;

/** One page of roster rows plus the metadata the table needs to render its pager. */
export interface PassengerListPage {
  items: PassengerDto[];
  /** Total rows matching the filter (across all pages). */
  total: number;
  page: number;
  pageSize: number;
}

/** A short-lived signed URL (download), with the instant it stops working. */
export interface SignedUrlDto {
  url: string;
  expiresAt: string;
}

/** A signed upload target: PUT the document bytes here, then attach the returned `key`. */
export interface UploadTargetDto {
  /** The private object key the bytes will live under (post back to attach). */
  key: string;
  /** The short-lived URL to PUT the bytes to. */
  url: string;
  method: "PUT";
  expiresAt: string;
}
